import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { UserWithOrdersDto } from '@/common/models/orders'
import { Header, PageTitle } from '@/components'
import {
  Button,
  Card,
  Checkbox,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import Loader from '../../../common/components/Loader'
import { Orders } from '../../../common/models'
import { IColumn } from '../../../core/components/Table'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  OrderPaginationParams,
  getAllOrders,
  getUsersOrders,
} from '../../../core/services/orders'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { OrderTable } from './OrderTable'
import { OrderUsersTable } from './OrderUsersTable'
import { getApiBaseURL } from '@/core/services/environment.ts'
import { IFestivalMenuItem } from '@/core/services/menu'

export const OrdersPage: React.FC = () => {
  const {
    page,
    setPage,
    sortBy,
    setSortBy,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    extraParams,
    setExtraParams,
  } = useTableParams({
    extraParams: {
      key: 'orders',
    },
  })

  const { editionId, festivalName } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'ORDERS' })
  const { hasEditPermission: hasExportPermission } = useCheckPermission({
    page: 'ORDERS_EXPORT',
  })

  const [orders, setOrders] = useState<Orders[]>([])
  const [usersOrders, setUsersOrders] = useState<UserWithOrdersDto[]>([])
  const d = new Date()
  d.setDate(d.getDate() - 30)
  const [startDate, setStartDate] = useState<number>(d.valueOf())
  const [endDate, setEndDate] = useState<number>(new Date().valueOf())
  const [showFailedOrders, setShowFailedOrders] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const timer = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useRef<{ [key: string]: string | null }>({})
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [selectedTab, setSelectedTab] = useState<'orders' | 'users'>(
    (extraParams.key as 'orders' | 'users') || 'orders',
  )

  const getOrdersData = useCallback(async () => {
    setLoading(true)
    try {
      const params: OrderPaginationParams = {
        page,
        size,
        sortBy,
        sortDirection,
        startDate,
        endDate,
        showFailedOrders,
      }

      params.buyer = searchParams.current.buyer
      params.email = searchParams.current.email
      params.displayableOrderNumber =
        searchParams.current.displayableOrderNumber
      params.barcode = searchParams.current.barcode

      const response = await getAllOrders(params)
      if (response.status === 200) {
        setOrders(response.data.content)
        setTotalElemenets(response.data.totalElements)
        setSize(response.data.size)
        setPage(response.data.pageNumber + 1)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
        setLoading(false)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    sortBy,
    setPage,
    setSize,
    setTotalElemenets,
    startDate,
    endDate,
    editionId,
    festivalName,
    sortDirection,
    showFailedOrders,
  ])

  const getUsersOrdersData = useCallback(async () => {
    setLoading(true)
    try {
      const params: OrderPaginationParams = {
        page,
        size,
        sortBy,
        sortDirection,
        startDate,
        endDate,
        showFailedOrders,
      }

      params.buyer = searchParams.current.buyer
      params.email = searchParams.current.email
      params.displayableOrderNumber =
        searchParams.current.displayableOrderNumber
      params.barcode = searchParams.current.barcode

      const response = await getUsersOrders(params)
      if (response.status === 200) {
        setUsersOrders(response.data.content)
        setTotalElemenets(response.data.totalElements)
        setSize(response.data.size)
        setPage(response.data.pageNumber + 1)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
        setLoading(false)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    sortBy,
    setPage,
    setSize,
    setTotalElemenets,
    startDate,
    endDate,
    editionId,
    festivalName,
    sortDirection,
    showFailedOrders,
  ])

  const exportDataCsv = async () => {
    const festivals: IFestivalMenuItem[] = JSON.parse(
      localStorage.getItem('festivals') || '[]',
    )
    const festivalId = festivals.filter(
      (f) => f.platformName === festivalName,
    )[0].id
    window.open(
      `${getApiBaseURL()}/operations/reports/orders/${festivalId}`,
      '_blank',
    )
  }

  useEffect(() => {
    if (selectedTab === 'orders') {
      getOrdersData()
    } else {
      getUsersOrdersData()
    }
  }, [getOrdersData, selectedTab])

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }

      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      const newTimer = setTimeout(async () => {
        setLoading(true)
        try {
          const params: OrderPaginationParams = {
            page: 0,
            size,
            sortBy,
            sortDirection,
          }

          params.buyer = searchParams.current.buyer
          params.email = searchParams.current.email
          params.displayableOrderNumber =
            searchParams.current.displayableOrderNumber
          params.barcode = searchParams.current.barcode

          if (selectedTab === 'orders') {
            const { data: res, status } = await getAllOrders(params)
            if (status === 200) {
              setOrders(res.content)
              setTotalElemenets(res.totalElements)
              setSize(res.size)
              setPage(res.pageNumber + 1)
            }
          } else {
            const { data: res, status } = await getUsersOrders(params)
            if (status === 200) {
              setUsersOrders(res.content)
              setTotalElemenets(res.totalElements)
              setSize(res.size)
              setPage(res.pageNumber + 1)
            }
          }
        } catch (err) {
          console.log(err)
        } finally {
          setLoading(false)
        }
      }, 2000)
      timer.current = newTimer
    },
    [page, size, sortBy, sortDirection, selectedTab],
  )

  const dateFilters = (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex flex-1 justify-center gap-3">
        <Checkbox
          checked={showFailedOrders}
          onCheckedChange={() => {
            setShowFailedOrders(!showFailedOrders)
          }}
        />
        <div className="text-body-medium text-monochrome-1">
          Show Failed Orders
        </div>
      </div>
      <div className="flex flex-1 min-w-[240px]">
        <DateTimePicker
          label="From:"
          labelClassName="flex flex-row items-center gap-2"
          value={new Date(startDate)}
          onChange={(date) => setStartDate(date?.valueOf() ?? Date.now())}
          placeholder="Select Time"
        />
      </div>
      <div className="flex flex-1 min-w-[240px]">
        <DateTimePicker
          labelClassName="flex flex-row items-center gap-2"
          label="To:"
          value={new Date(endDate)}
          onChange={(date) => setEndDate(date?.valueOf() ?? Date.now())}
          placeholder="Select Time"
        />
      </div>
    </div>
  )

  return (
    <>
      <Header
        pageTitle="Orders"
        actions={
          <>
            {hasExportPermission && (
              <Button
                variant="secondary"
                className="border border-monochrome-120"
                size="sm"
              >
                Export Left In Bag CSV
              </Button>
            )}
            {hasExportPermission && (
              <Button onClick={exportDataCsv} size="sm">
                Export Data CSV
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}

      <Tabs
        value={selectedTab}
        onValueChange={(newKey) => {
          setSelectedTab(newKey as 'orders' | 'users')
          setExtraParams((s) => ({ ...s, key: newKey }))
          setPage(1)
        }}
        className="w-full"
      >
        <TabsList className="grid w-full mb-6">
          <div className="bg-white/10 p-2.5 rounded-[20px]">
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
          </div>
        </TabsList>
        <Card className="!bg-monochrome-4-60 p-5 backdrop-blur-lg max-h-[calc(100svh-280px)] overflow-hidden">
          <div className="flex items-center gap-4 justify-between">
            <PageTitle
              title={selectedTab === 'orders' ? 'Orders' : 'Users'}
              icon={selectedTab === 'orders' ? 'bag-2' : 'profile-2user'}
              className="mb-5"
            />
            {dateFilters}
          </div>

          <TabsContent value="orders" className="mt-0">
            <OrderTable
              maxHeight="calc(100vh - 380px)"
              handleSearchParams={handleSearchParams}
              hasEditPermission={hasEditPermission}
              sortBy={sortBy}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortDirection={sortDirection}
              orders={orders}
              loading={loading}
              noDataMessage={
                searchParams.current.email ? (
                  <span>
                    Sorry, <b>Email</b> not found
                  </span>
                ) : searchParams.current.displayableOrderNumber ? (
                  <span>
                    Sorry, <b>Order Number</b> not found
                  </span>
                ) : searchParams.current.barcode ? (
                  <span>
                    Sorry, <b>Barcode</b> not found
                  </span>
                ) : (
                  <span>Sorry, There are no data</span>
                )
              }
            />
          </TabsContent>

          <TabsContent value="users" className="mt-0">
            <OrderUsersTable
              maxHeight="calc(100vh - 380px)"
              handleSearchParams={handleSearchParams}
              hasEditPermission={hasEditPermission}
              sortBy={sortBy}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortDirection={sortDirection}
              orders={usersOrders}
              loading={loading}
              noDataMessage={
                searchParams.current.email ? (
                  <span>
                    Sorry, <b>Email</b> not found
                  </span>
                ) : searchParams.current.displayableOrderNumber ? (
                  <span>
                    Sorry, <b>Order Number</b> not found
                  </span>
                ) : searchParams.current.barcode ? (
                  <span>
                    Sorry, <b>Barcode</b> not found
                  </span>
                ) : (
                  <span>Sorry, There are no data</span>
                )
              }
            />
          </TabsContent>
        </Card>
      </Tabs>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
